import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import CreditHistory from "./CreditHistory";
import OrderHistory from "./OrderHistory";
import InvoiceHistory from "./InvoiceHistory";
import QuoteHistory from "./QuoteHistory";
import { ItemHistory } from "./ItemHistory";

// Configuration for history types - easily expandable
const HISTORY_TYPES = [
  {
    id: "orders",
    label: "Order History",
    component: OrderHistory,
  },
  {
    id: "credits",
    label: "Credit History",
    component: CreditHistory,
  },
  {
    id: "invoices",
    label: "Invoice History",
    component: InvoiceHistory,
  },
  {
    id: "quotes",
    label: "Quote History",
    component: QuoteHistory,
  },
  {
    id: "items",
    label: "Item History",
    component: ItemHistory,
  },
];

export default function CustomerHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const historyParam = searchParams.get("history") || "orders";

  const [activeHistoryType, setActiveHistoryType] = useState(historyParam);
  const [loadedComponents, setLoadedComponents] = useState(
    new Set([historyParam])
  );

  // Update active tab when URL changes
  useEffect(() => {
    const urlHistory = searchParams.get("history") || "orders";
    if (urlHistory !== activeHistoryType) {
      setActiveHistoryType(urlHistory);
      if (!loadedComponents.has(urlHistory)) {
        setLoadedComponents((prev) => new Set([...prev, urlHistory]));
      }
    }
  }, [searchParams, activeHistoryType, loadedComponents]);

  const handleTabChange = (historyTypeId) => {
    setActiveHistoryType(historyTypeId);
    // Update URL
    setSearchParams({ history: historyTypeId });
    // Mark this component as loaded when first accessed
    if (!loadedComponents.has(historyTypeId)) {
      setLoadedComponents((prev) => new Set([...prev, historyTypeId]));
    }
  };

  const renderAllComponents = () => {
    return HISTORY_TYPES.map((historyType) => {
      const Component = historyType.component;
      const isLoaded = loadedComponents.has(historyType.id);
      const isActive = activeHistoryType === historyType.id;

      return (
        <div
          key={historyType.id}
          style={{
            display: isActive ? "block" : "none",
          }}
        >
          {/* Only render the component if it has been loaded (accessed) */}
          {isLoaded ? <Component /> : null}
        </div>
      );
    });
  };

  return (
    <div className="p-6">
      {/* Toggle Buttons */}
      <div className="flex justify-around bg-gray-50 rounded-lg p-2 mb-6">
        {HISTORY_TYPES.map((historyType) => (
          <button
            key={historyType.id}
            onClick={() => handleTabChange(historyType.id)}
            className={`flex-1 px-4 py-2 mx-1 rounded-md text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              activeHistoryType === historyType.id
                ? "bg-[var(--primary-color)] text-white shadow-sm"
                : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
            }`}
          >
            {historyType.label}
          </button>
        ))}
      </div>

      {/* Active Component */}
      <div>{renderAllComponents()}</div>
    </div>
  );
}
